/**
 * Notifications Panel Component
 * Alpine.js component for the notifications slide-out panel
 */

import NotificationsAPI from '../modules/notifications-api.js';

/**
 * Notifications Panel Alpine Component
 * @returns {object} Alpine component
 */
export function notificationsPanel() {
    return {
        // Panel state
        isOpen: false,
        isLoading: false,
        loadError: null,
        _initialized: false,

        // User ID (passed from init)
        userId: null,

        // Counters
        counters: {
            friendRequests: 0,
            notifications: 0,
            views: 0
        },

        // Section data
        friendRequests: [],
        friendRequestsExpanded: false,
        friendRequestsLoading: false,
        friendRequestsLimit: 2,

        views: [],
        viewsLoading: false,

        notifications: [],
        notificationsLoading: false,
        notificationsOffset: 0,
        hasMoreNotifications: true,

        // Processing states for friend requests
        processingRequests: {},

        /**
         * Initialize the component
         * @param {string} userId - Current user ID
         */
        async init(userId) {
            // Skip if no valid userId
            if (!userId || userId === 'undefined' || userId === 'null') {
                return;
            }

            // Prevent double initialization
            if (this._initialized) {
                return;
            }
            this._initialized = true;

            this.userId = String(userId);

            // Listen for panel toggle events
            window.addEventListener('notifications:toggle', () => this.toggle());
            window.addEventListener('notifications:open', () => this.open());
            window.addEventListener('notifications:close', () => this.close());

            // Keep the bell badge live: a new friend request arrives over the
            // websocket as a frNewCnt COUNTER_UPDATE (dispatched by
            // websocket-client.js). Re-fetch the authoritative friend-request /
            // notification / view counts so the badge reflects it without a
            // page refresh, instead of only updating when the panel is opened.
            document.addEventListener('ws:counter-update', (event) => {
                if (event.detail && event.detail.id === 'frNewCnt') {
                    this.loadCounters();
                }
            });

            // Mobile: use History API so the panel acts like a page
            if (this._isMobile()) {
                // Close panel when user presses back
                window.addEventListener('popstate', (e) => {
                    if (this.isOpen && !(e.state && e.state.notificationsOpen)) {
                        this._closeSilent();
                    }
                });

                // Re-open panel when returning via bfcache (swipe back from another page)
                window.addEventListener('pageshow', (e) => {
                    if (e.persisted && history.state && history.state.notificationsOpen) {
                        this._openSilent();
                    }
                });

                // If page loaded with notificationsOpen in history state (non-bfcache back nav)
                if (history.state && history.state.notificationsOpen) {
                    this._openSilent();
                }

                // Clean up history state when navigating away (link click, form submit).
                // Without this, the notificationsOpen entry stays in the history stack,
                // causing the panel to re-open when the user navigates back.
                window.addEventListener('pagehide', (e) => {
                    if (this.isOpen && !e.persisted) {
                        // Page is being unloaded (not cached) — replace the history
                        // entry so back-navigation doesn't see notificationsOpen.
                        try {
                            if (history.state && history.state.notificationsOpen) {
                                history.replaceState(this._stateWithoutPanel(), '');
                            }
                        } catch { /* some browsers restrict history API during unload */ }
                    }
                });

                // Close panel when bottom nav links are clicked
                const bottomNav = document.querySelector('[data-component="mobile-bottom-nav"]');
                if (bottomNav) {
                    bottomNav.addEventListener('click', (e) => {
                        const link = e.target.closest('a[href]');
                        if (link && this.isOpen) {
                            // Silently close the panel and clean up the history entry
                            this._closeSilent();
                            if (history.state && history.state.notificationsOpen) {
                                history.replaceState(this._stateWithoutPanel(), '');
                            }
                        }
                    });
                }
            }
        },

        /**
         * Toggle panel open/closed
         */
        async toggle() {
            if (this.isOpen) {
                this.close();
            } else {
                await this.open();
            }
        },

        // The entry can also carry the Message requests view's marker, which stays.
        _stateWithoutPanel() {
            const rest = { ...(history.state || {}) };
            delete rest.notificationsOpen;
            return rest;
        },

        /**
         * Open the panel and load data
         */
        async open() {
            // On mobile, push a history entry so back button closes the panel.
            // Must happen before awaiting data load so back works during loading.
            if (this._isMobile()) {
                // Over the Message requests view, the entry belongs to the view.
                const state = { notificationsOpen: true };
                history.pushState(window.PmRequests?.entryState?.(state) ?? state, '');
            }

            await this._openSilent();
        },

        /**
         * Open without pushing history (used by popstate/pageshow handlers)
         */
        async _openSilent() {
            this.isOpen = true;
            this.loadError = null;
            document.body.classList.add('notifications-panel-open');
            this._highlightNotificationsNav();

            if (this.notifications.length === 0 && this.friendRequests.length === 0 && this.views.length === 0) {
                await this.loadAllData();
            } else {
                this.isLoading = false;
            }
        },

        /**
         * Close the panel
         */
        close() {
            // On mobile, go back in history instead of just closing
            if (this._isMobile() && this.isOpen && history.state && history.state.notificationsOpen) {
                history.back();
                return;
            }
            this._closeSilent();
        },

        /**
         * Close without touching history (used by popstate handler)
         */
        _closeSilent() {
            this.isOpen = false;
            document.body.classList.remove('notifications-panel-open');
            this._restorePageNav();
        },

        /**
         * Load all notification data
         */
        async loadAllData() {
            this.isLoading = true;
            this.loadError = null;

            try {
                // Load all sections in parallel with timeout
                const timeoutPromise = new Promise((_, reject) =>
                    setTimeout(() => reject(new Error('Loading timed out')), 15000)
                );

                await Promise.race([
                    Promise.all([
                        this.loadCounters(),
                        this.loadFriendRequests(),
                        this.loadViews(),
                        this.loadNotifications()
                    ]),
                    timeoutPromise
                ]);

                // Auto-mark as read after loading
                this.autoMarkAsRead();
            } catch (error) {
                console.error('Failed to load notifications data:', error);
                this.loadError = error.message || 'Failed to load notifications';
            } finally {
                this.isLoading = false;
            }
        },

        /**
         * Load user counters
         */
        async loadCounters() {
            try {
                const data = await NotificationsAPI.fetchUserCounters();
                this.counters = {
                    friendRequests: data.new_friend_requests || 0,
                    notifications: data.new_notifications || 0,
                    views: data.new_views || 0
                };

                // Update badge in navigation
                this.updateNavBadge();
            } catch (error) {
                console.error('Failed to load counters:', error);
            }
        },

        /**
         * Load friend requests
         */
        async loadFriendRequests() {
            if (!this.userId) return;

            this.friendRequestsLoading = true;
            try {
                const data = await NotificationsAPI.fetchFriendRequests(this.userId, 25, 0);
                this.friendRequests = data.data || [];
            } catch (error) {
                console.error('Failed to load friend requests:', error);
            } finally {
                this.friendRequestsLoading = false;
            }
        },

        /**
         * Load profile views
         */
        async loadViews() {
            if (!this.userId) return;

            this.viewsLoading = true;
            try {
                const data = await NotificationsAPI.fetchProfileViews(this.userId, 20, 0);
                this.views = data.data || [];
            } catch (error) {
                console.error('Failed to load profile views:', error);
            } finally {
                this.viewsLoading = false;
            }
        },

        /**
         * Load activity notifications (initial load only)
         */
        async loadNotifications() {
            this.notificationsLoading = true;
            try {
                const data = await NotificationsAPI.fetchNotifications(10, 0);
                const rawResults = data.data || [];
                const filtered = rawResults.filter(n =>
                    n.type !== 'FRIEND_REQUEST_MESSAGE' &&
                    // System notifications (no sender) are kept. The deleted-user
                    // guard only applies to sender-bearing activity.
                    (!n.user || n.user.status !== 'deleted')
                );

                // Deduplicate by ID
                const seen = new Set();
                this.notifications = filtered.filter(n => {
                    const id = String(n.id);
                    if (seen.has(id)) return false;
                    seen.add(id);
                    return true;
                });

                this.notificationsOffset = 0;
                this.hasMoreNotifications = rawResults.length >= 10;
            } catch (error) {
                console.error('Failed to load notifications:', error);
            } finally {
                this.notificationsLoading = false;
            }
        },

        /**
         * Load more notifications (pagination)
         */
        async loadMoreNotifications() {
            if (this.notificationsLoading || !this.hasMoreNotifications) return;

            // Set loading state immediately to prevent double-clicks
            this.notificationsLoading = true;
            this.notificationsOffset += 10;

            try {
                const data = await NotificationsAPI.fetchNotifications(10, this.notificationsOffset);
                const rawResults = data.data || [];
                const filtered = rawResults.filter(n =>
                    n.type !== 'FRIEND_REQUEST_MESSAGE' &&
                    // System notifications (no sender) are kept. The deleted-user
                    // guard only applies to sender-bearing activity.
                    (!n.user || n.user.status !== 'deleted')
                );

                // Deduplicate by ID to prevent Alpine key conflicts
                const existingIds = new Set(this.notifications.map(n => String(n.id)));
                const uniqueNew = filtered.filter(n => !existingIds.has(String(n.id)));

                if (uniqueNew.length > 0) {
                    this.notifications = [...this.notifications, ...uniqueNew];
                }

                // Only show "load more" if we got a full page of results
                this.hasMoreNotifications = rawResults.length >= 10;
            } catch (error) {
                console.error('Failed to load more notifications:', error);
                // On error, decrement offset to allow retry
                this.notificationsOffset -= 10;
            } finally {
                this.notificationsLoading = false;
            }
        },

        /**
         * Accept a friend request
         * @param {object} request
         */
        async acceptFriendRequest(request) {
            const userId = String(request.user?.id || request.id);
            if (this.processingRequests[userId]) return;

            this.processingRequests[userId] = true;

            try {
                await NotificationsAPI.acceptFriendRequest(userId);

                // Remove from list
                this.friendRequests = this.friendRequests.filter(r =>
                    String(r.user?.id || r.id) !== userId
                );

                // Update counter
                this.counters.friendRequests = Math.max(0, this.counters.friendRequests - 1);
                this.updateNavBadge();
            } catch (error) {
                console.error('Failed to accept friend request:', error);
            } finally {
                this.processingRequests[userId] = false;
            }
        },

        /**
         * Ignore a friend request
         * @param {object} request
         */
        async ignoreFriendRequest(request) {
            const userId = String(request.user?.id || request.id);
            if (this.processingRequests[userId]) return;

            this.processingRequests[userId] = true;

            try {
                await NotificationsAPI.ignoreFriendRequest(userId);

                // Remove from list
                this.friendRequests = this.friendRequests.filter(r =>
                    String(r.user?.id || r.id) !== userId
                );

                // Update counter
                this.counters.friendRequests = Math.max(0, this.counters.friendRequests - 1);
                this.updateNavBadge();
            } catch (error) {
                console.error('Failed to ignore friend request:', error);
            } finally {
                this.processingRequests[userId] = false;
            }
        },

        /**
         * Check if a request is being processed
         * @param {object} request
         * @returns {boolean}
         */
        isProcessing(request) {
            const userId = String(request.user?.id || request.id);
            return !!this.processingRequests[userId];
        },

        /**
         * Mark all notifications as read
         */
        async markAllAsRead() {
            try {
                const promises = [];
                if (this.counters.notifications > 0) {
                    promises.push(NotificationsAPI.markAllNotificationsRead());
                }
                if (this.counters.views > 0 && this.userId) {
                    promises.push(NotificationsAPI.resetViewsCounter(this.userId));
                }
                await Promise.all(promises);

                // Update local counters
                this.counters.notifications = 0;
                this.counters.views = 0;
                this.updateNavBadge();

                // Mark all notifications as read in list
                this.notifications = this.notifications.map(n => ({ ...n, uread: false }));
            } catch (error) {
                console.error('Failed to mark all as read:', error);
            }
        },

        /**
         * Auto-mark as read when panel opens
         */
        autoMarkAsRead() {
            // Small delay to let user see unread count first
            setTimeout(() => {
                if (this.isOpen && (this.counters.notifications > 0 || this.counters.views > 0)) {
                    this.markAllAsRead();
                }
            }, 2000);
        },

        /**
         * Update navigation badge with total count
         */
        updateNavBadge() {
            const total = this.counters.friendRequests + this.counters.notifications + this.counters.views;

            // Update sidebar badge. Badges are always rendered (hidden inline when
            // zero) so the bell can appear the moment a friend request arrives.
            // Visibility uses an !important inline display so it wins over the
            // collapsed/expanded sidebar CSS rules; clearing it lets those class
            // rules pick the right variant again.
            const sidebarBadges = document.querySelectorAll('[data-notifications-badge]');
            sidebarBadges.forEach(badge => {
                if (total > 0) {
                    const count = total > 99 ? '99+' : total;
                    // Check if badge uses classic parentheses format
                    if (badge.dataset.badgeFormat === 'parens') {
                        badge.textContent = '(+' + count + ')';
                    } else {
                        badge.textContent = count;
                    }
                    badge.classList.remove('hidden');
                    badge.style.removeProperty('display');
                } else {
                    badge.textContent = '';
                    badge.style.setProperty('display', 'none', 'important');
                }
            });

            // Dispatch event for other components
            window.dispatchEvent(new CustomEvent('notifications:count-updated', {
                detail: { total, counters: this.counters }
            }));
        },

        /**
         * Get displayed friend requests (limited)
         * @returns {Array}
         */
        get displayedFriendRequests() {
            if (this.friendRequestsExpanded) {
                return this.friendRequests;
            }
            return this.friendRequests.slice(0, this.friendRequestsLimit);
        },

        /**
         * Get total notifications count for badge
         * @returns {number}
         */
        get totalCount() {
            return this.counters.friendRequests + this.counters.notifications + this.counters.views;
        },

        /**
         * Get notification URL
         * @param {object} notification
         * @returns {string}
         */
        getNotificationUrl(notification) {
            return NotificationsAPI.getNotificationUrl(notification);
        },

        /**
         * Whether a notification links somewhere on the web frontend. System
         * types with no web destination render as static, non-clickable rows.
         * @param {object} notification
         * @returns {boolean}
         */
        isNotificationClickable(notification) {
            return NotificationsAPI.isNotificationClickable(notification);
        },

        /**
         * Get notification icon info
         * @param {string} type
         * @returns {object}
         */
        getNotificationIcon(type) {
            return NotificationsAPI.getNotificationIcon(type);
        },

        /**
         * Get notification action text
         * @param {string} type
         * @param {string} [gender] - User gender for localized verb forms
         * @returns {string}
         */
        getActionText(type, gender) {
            return NotificationsAPI.getNotificationActionText(type, gender);
        },

        /**
         * Get notification badge SVG icon
         * @param {string} type
         * @returns {string}
         */
        getNotificationBadgeSvg(type) {
            return NotificationsAPI.getNotificationBadgeSvg(type);
        },

        /**
         * Format relative time
         * @param {string} dateString
         * @returns {string}
         */
        formatTime(dateString) {
            return NotificationsAPI.formatRelativeTime(dateString);
        },

        /**
         * Format relative time in concise format (5s, 2m, 3h, 2d)
         * @param {string} dateString
         * @returns {string}
         */
        formatTimeConcise(dateString) {
            return NotificationsAPI.formatRelativeTimeConcise(dateString);
        },

        /**
         * Highlight the notifications nav item and dim the page-level active item
         */
        _highlightNotificationsNav() {
            // Mobile bottom nav: bold the bell icon
            const bellBtn = document.querySelector('[data-action="toggle-notifications"]');
            if (bellBtn) {
                bellBtn.classList.add('nav-active', 'text-gray-900');
                bellBtn.classList.remove('text-gray-500');
                // Un-bold the currently active page nav item
                const activeNavItem = document.querySelector('[data-component="mobile-bottom-nav"] .nav-active:not([data-action="toggle-notifications"])');
                if (activeNavItem) {
                    activeNavItem.classList.remove('nav-active', 'text-gray-900');
                    activeNavItem.classList.add('text-gray-500');
                    activeNavItem.dataset.wasActiveNav = 'true';
                }
            }

            const sidebar = document.querySelector('[data-sidebar="true"]');
            if (!sidebar) return;

            // Remove active state from the current page-level nav item
            const activeItem = sidebar.querySelector('.sidebar-nav-item.bg-primary');
            if (activeItem) {
                activeItem.classList.remove('bg-primary', 'text-white');
                activeItem.classList.add('text-gray-800', 'hover:text-black');
                activeItem.dataset.wasActive = 'true';

                // Reset icon color
                const iconContainer = activeItem.querySelector('.sidebar-nav-icon');
                if (iconContainer) {
                    iconContainer.classList.remove('text-white');
                    iconContainer.classList.add('text-gray-600', 'group-hover:text-gray-800');
                }

                // Reset label color
                const label = activeItem.querySelector('.sidebar-nav-label');
                if (label) {
                    label.classList.remove('text-white');
                    label.classList.add('text-gray-700', 'group-hover:text-gray-900');
                }
            }

            // Add active state to the notifications button
            const notifBtn = sidebar.querySelector('[data-nav-item="notifications"]');
            if (notifBtn) {
                notifBtn.classList.add('bg-primary', 'text-white');
                notifBtn.classList.remove('text-gray-800', 'hover:text-black');

                const iconContainer = notifBtn.querySelector('.sidebar-nav-icon');
                if (iconContainer) {
                    iconContainer.classList.remove('text-gray-600', 'group-hover:text-gray-800');
                    iconContainer.classList.add('text-white');
                }

                const label = notifBtn.querySelector('.sidebar-nav-label');
                if (label) {
                    label.classList.remove('text-gray-700', 'group-hover:text-gray-900');
                    label.classList.add('text-white');
                }
            }
        },

        /**
         * Restore the page-level nav highlight and remove notifications highlight
         */
        _restorePageNav() {
            // Mobile bottom nav: restore bell and previous active item
            const bellBtn = document.querySelector('[data-action="toggle-notifications"]');
            if (bellBtn) {
                bellBtn.classList.remove('nav-active', 'text-gray-900');
                bellBtn.classList.add('text-gray-500');
                const wasActiveNav = document.querySelector('[data-component="mobile-bottom-nav"] [data-was-active-nav="true"]');
                if (wasActiveNav) {
                    wasActiveNav.classList.add('nav-active', 'text-gray-900');
                    wasActiveNav.classList.remove('text-gray-500');
                    delete wasActiveNav.dataset.wasActiveNav;
                }
            }

            const sidebar = document.querySelector('[data-sidebar="true"]');
            if (!sidebar) return;

            // Remove active state from notifications button
            const notifBtn = sidebar.querySelector('[data-nav-item="notifications"]');
            if (notifBtn) {
                notifBtn.classList.remove('bg-primary', 'text-white');
                notifBtn.classList.add('text-gray-800', 'hover:text-black');

                const iconContainer = notifBtn.querySelector('.sidebar-nav-icon');
                if (iconContainer) {
                    iconContainer.classList.add('text-gray-600', 'group-hover:text-gray-800');
                    iconContainer.classList.remove('text-white');
                }

                const label = notifBtn.querySelector('.sidebar-nav-label');
                if (label) {
                    label.classList.add('text-gray-700', 'group-hover:text-gray-900');
                    label.classList.remove('text-white');
                }
            }

            // Restore active state to the page-level nav item
            const wasActive = sidebar.querySelector('.sidebar-nav-item[data-was-active="true"]');
            if (wasActive) {
                wasActive.classList.add('bg-primary', 'text-white');
                wasActive.classList.remove('text-gray-800', 'hover:text-black');
                delete wasActive.dataset.wasActive;

                const iconContainer = wasActive.querySelector('.sidebar-nav-icon');
                if (iconContainer) {
                    iconContainer.classList.add('text-white');
                    iconContainer.classList.remove('text-gray-600', 'group-hover:text-gray-800');
                }

                const label = wasActive.querySelector('.sidebar-nav-label');
                if (label) {
                    label.classList.add('text-white');
                    label.classList.remove('text-gray-700', 'group-hover:text-gray-900');
                }
            }
        },

        /**
         * Handle clicking outside to close
         * @param {Event} event
         */
        handleClickOutside(event) {
            const panel = this.$refs.panel;
            const trigger = document.querySelector('[data-notifications-trigger]');

            if (panel && !panel.contains(event.target) && trigger && !trigger.contains(event.target)) {
                this.close();
            }
        },

        /**
         * Check if viewport matches mobile breakpoint (below Tailwind sm: 640px)
         * @returns {boolean}
         */
        _isMobile() {
            return window.matchMedia('(max-width: 639px)').matches;
        }
    };
}

// Export for module usage
export default notificationsPanel;

// Global function to toggle notifications panel
window.toggleNotificationsPanel = function() {
    window.dispatchEvent(new CustomEvent('notifications:toggle'));
};

window.openNotificationsPanel = function() {
    window.dispatchEvent(new CustomEvent('notifications:open'));
};

window.closeNotificationsPanel = function() {
    window.dispatchEvent(new CustomEvent('notifications:close'));
};
